import React, { useState } from 'react';

/**
 * 🔒 PrivacyPolicyModal — Informativa Estesa Privacy e Trattamento Dati (GDPR UE 2016/679)
 * Consultabile direttamente dalla Dashboard da tutti i Counselor e Admin.
 */
const PrivacyPolicyModal = ({ isOpen, onClose }) => {
    const [activeSection, setActiveSection] = useState('all');

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-3 md:p-6 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-slate-100">
                
                {/* Header */}
                <div className="p-5 md:p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/80">
                    <div className="flex items-center gap-3.5">
                        <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-2xl shadow-xs">
                            🛡️
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-black uppercase tracking-widest text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-200">
                                    GDPR UE 2016/679
                                </span>
                                <span className="text-[10px] font-bold text-slate-400">Artt. 13 & 14</span>
                            </div>
                            <h2 className="text-lg md:text-xl font-bold text-slate-800 mt-0.5">
                                Informativa Privacy & Trattamento Dati
                            </h2>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-200/60 transition cursor-pointer text-xl"
                    >
                        ✕
                    </button>
                </div>

                {/* Content */}
                <div className="p-5 md:p-8 overflow-y-auto space-y-6 text-slate-700 text-xs md:text-sm leading-relaxed">
                    
                    {/* Intro Banner */}
                    <div className="p-4 bg-indigo-50/60 border border-indigo-100 rounded-2xl flex items-start gap-3">
                        <span className="text-xl mt-0.5">ℹ️</span>
                        <div>
                            <p className="font-bold text-indigo-950 text-xs md:text-sm">Trasparenza e Segreto Professionale in AwarenessTool</p>
                            <p className="text-slate-600 text-xs mt-1">
                                Questa informativa illustra nel dettaglio quali dati vengono raccolti, come sono archiviati e protetti nei percorsi di <strong>Counseling Individuale</strong> e <strong>Counseling Organizzativo / People Care</strong>.
                            </p>
                        </div>
                    </div>

                    {/* Section 1: Titolare e Piattaforma */}
                    <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-2">
                        <h3 className="font-bold text-slate-900 text-sm md:text-base flex items-center gap-2 text-indigo-900">
                            <span>🏛️</span> 1. Titolare del Trattamento e Infrastruttura
                        </h3>
                        <p className="text-slate-600">
                            <strong>Titolare del Trattamento:</strong> Il Counselor incaricato e l'Ente committente per i percorsi organizzativi.
                        </p>
                        <p className="text-slate-600">
                            <strong>Infrastruttura Cloud e Sicurezza:</strong> AwarenessTool è ospitato su data center certificati Google Cloud / Firebase (conformi a ISO 27001, SOC 2 e GDPR) con crittografia end-to-end <strong>AES-256</strong> e comunicazioni protette via protocollo <strong>HTTPS/TLS</strong>.
                        </p>
                    </div>

                    {/* Section 2: Dati Raccolti */}
                    <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-3">
                        <h3 className="font-bold text-slate-900 text-sm md:text-base flex items-center gap-2 text-indigo-900">
                            <span>📋</span> 2. Dati Raccolti e Trattati nella Piattaforma
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                            <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/60 space-y-1.5">
                                <h4 className="font-bold text-xs uppercase tracking-wide text-slate-800 flex items-center gap-1.5">
                                    <span>👤</span> A. Percorso Individuale
                                </h4>
                                <ul className="list-disc list-inside text-xs text-slate-600 space-y-1">
                                    <li><strong>Anagrafica & Contatti:</strong> Nome, email, telefono, dati contrattuali.</li>
                                    <li><strong>Diari & Note Introspettive:</strong> Riflessioni personali e trascrizioni audio.</li>
                                    <li><strong>Lavoro Esperienziale:</strong> Sogni, sedia vuota, carte creative, respirazione e grounding.</li>
                                    <li><strong>Analisi Transazionale:</strong> Stati dell'Io e Triangolo Drammatico.</li>
                                    <li><strong>Ruota della Vita:</strong> Autovalutazione quantitativa degli ambiti di vita.</li>
                                </ul>
                            </div>

                            <div className="p-3.5 bg-teal-50/50 rounded-xl border border-teal-200/70 space-y-1.5">
                                <h4 className="font-bold text-xs uppercase tracking-wide text-teal-950 flex items-center gap-1.5">
                                    <span>🏢</span> B. Percorso Organizzativo (People Care)
                                </h4>
                                <ul className="list-disc list-inside text-xs text-slate-700 space-y-1">
                                    <li><strong>Dati di Contesto:</strong> Ente di appartenenza (BuenaVista), ruolo, referente.</li>
                                    <li><strong>Questionari IN & OUT:</strong>
                                        <ul className="list-none pl-3 space-y-0.5 mt-0.5 text-[11px] text-slate-600">
                                            <li>• <em>Sez. A:</em> Bisogni organizzativi rilevati (carichi, motivazione, clima).</li>
                                            <li>• <em>Sez. B:</em> Valutazione competenze (comunicazione, gestione emotiva, problem solving).</li>
                                            <li>• <em>Sez. C:</em> NPS e gradimento del percorso.</li>
                                        </ul>
                                    </li>
                                    <li><strong>Verbali di Gruppo:</strong> Dinamiche e sintesi metodologiche d'equipe.</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Section 3: Segregazione e Riservatezza */}
                    <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-2">
                        <h3 className="font-bold text-slate-900 text-sm md:text-base flex items-center gap-2 text-indigo-900">
                            <span>🔐</span> 3. Segregazione dei Dati & Segreto Deontologico
                        </h3>
                        <p className="text-slate-600">
                            AwarenessTool implementa regole di isolamento granulare dei dati:
                        </p>
                        <div className="space-y-2 pt-1">
                            <div className="flex items-start gap-2 text-xs">
                                <span className="text-emerald-600 font-bold">✓</span>
                                <span><strong>Isolamento Clinico:</strong> I diari, le note di colloquio e i contenuti intimi sono visibili <em>esclusivamente</em> al cliente e al counselor a cui è assegnato in carico.</span>
                            </div>
                            <div className="flex items-start gap-2 text-xs">
                                <span className="text-emerald-600 font-bold">✓</span>
                                <span><strong>Scudo per la Supervisione:</strong> L'accesso da parte dei supervisori a cartelle non proprie richiede esplicita autorizzazione tracciata nel registro di audit.</span>
                            </div>
                            <div className="flex items-start gap-2 text-xs">
                                <span className="text-emerald-600 font-bold">✓</span>
                                <span><strong>Restituzione Anonima per gli Enti:</strong> All'azienda o all'ente committente vengono restituiti unicamente report aggregati e statistici (medie, percentuali, delta IN/OUT) senza alcuna attribuzione nominale dei bisogni o delle note private.</span>
                            </div>
                        </div>
                    </div>

                    {/* Section 4: AI & Diritti GDPR */}
                    <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-2">
                        <h3 className="font-bold text-slate-900 text-sm md:text-base flex items-center gap-2 text-indigo-900">
                            <span>⚖️</span> 4. Elaborazione AI & Diritti dell'Interessato (Artt. 15-22 GDPR)
                        </h3>
                        <p className="text-slate-600 text-xs">
                            Gli strumenti di intelligenza artificiale opzionali sono adoperati solo per facilitare trascrizioni e sintesi cliniche; nessun dato viene impiegato per addestrare modelli pubblici né ceduto a soggetti terzi.
                        </p>
                        <p className="text-slate-600 text-xs">
                            Ciascun utente ha il pieno diritto di richiedere l'accesso, la rettifica, la portabilità dei dati in PDF/JSON e la cancellazione definitiva (diritto all'oblio) inviando una comunicazione a <a href="mailto:info@awarenesstool.it" className="text-indigo-600 font-bold underline">info@awarenesstool.it</a>.
                        </p>
                    </div>

                </div>

                {/* Footer */}
                <div className="p-4 md:p-5 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                    <button
                        onClick={() => window.print()}
                        className="px-4 py-2 text-slate-700 bg-white border border-slate-200 rounded-xl text-xs font-bold hover:bg-slate-100 transition flex items-center gap-1.5 cursor-pointer shadow-2xs"
                    >
                        <span>🖨️</span> Stampa / Salva PDF
                    </button>
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition cursor-pointer shadow-sm active:scale-95"
                    >
                        Chiudi
                    </button>
                </div>

            </div>
        </div>
    );
};

export default PrivacyPolicyModal;
